import { DataGrid } from '@thingslog/ui-components';
import React, { FC, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GridValueGetterParams } from '@mui/x-data-grid';
import { GridRenderCellParams, GridColDef } from '@thingslog/ui-components';
import { format } from 'date-fns';
import {
  Tooltip,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ReportConfigDto } from '@thingslog/repositories';
import { scheduledReportsQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import { useToast } from '@thingslog/ui-components';

const ScheduledReportConfigsTable: FC<ScheduledReportConfigsTableProps> = ({
  reportConfigRows,
  onRefetch,
}: ScheduledReportConfigsTableProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [size, setSize] = useState<number>(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [reportToDelete, setReportToDelete] = useState<string | null>(null);

  const { useDeleteReportConfig } = scheduledReportsQueryClient;
  const { mutate: deleteConfig, isLoading: isDeleting } = useDeleteReportConfig();

  const handleDeleteClick = (reportName: string): void => {
    setReportToDelete(reportName);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = (): void => {
    if (reportToDelete) {
      deleteConfig(reportToDelete, {
        onSuccess: () => {
          toast({
            type: 'success',
            message: t('report_configs_delete_success'),
            duration: 3000,
          });
          onRefetch();
          setDeleteDialogOpen(false);
          setReportToDelete(null);
        },
        onError: (error: unknown) => {
          const axiosError = error as { response?: { data?: { message?: string } } };
          toast({
            type: 'error',
            message: axiosError.response?.data?.message || t('report_configs_delete_error'),
            duration: 5000,
          });
        },
      });
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  const renderCellTooltip = (params: GridRenderCellParams): ReactNode => {
    return (
      <Tooltip key={params.id} title={params.value || ''} arrow>
        <span>{params.value}</span>
      </Tooltip>
    );
  };

  const renderReportType = (params: GridRenderCellParams): ReactNode => {
    const reportType = params.value;
    if (!reportType) return null;

    return <Chip label={reportType.replace(/_/g, ' ')} color="primary" size="small" />;
  };

  const renderOccurrence = (params: GridRenderCellParams): ReactNode => {
    const occurrence = params.value;
    if (!occurrence) return null;

    return <Chip label={occurrence} color="success" size="small" />;
  };

  const renderDeviceGroups = (params: GridRenderCellParams): ReactNode => {
    const deviceGroups = params.value as string[];
    if (!deviceGroups || deviceGroups.length === 0) return null;

    return (
      <Tooltip title={deviceGroups.join(', ')} arrow>
        <span>{deviceGroups.length} group(s)</span>
      </Tooltip>
    );
  };

  const renderEmailList = (params: GridRenderCellParams): ReactNode => {
    const emailList = params.value as string;
    if (!emailList || emailList.trim() === '') return null;

    const emails = emailList
      .split(',')
      .map((email: string) => email.trim())
      .filter((email: string) => email !== '');
    if (emails.length === 0) return null;

    return (
      <Tooltip title={emails.join(', ')} arrow>
        <span>{emails.length} email(s)</span>
      </Tooltip>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'reportName',
      headerName: t('report_configs_name_header'),
      flex: 1.5,
      minWidth: 200,
      renderCell: renderCellTooltip,
    },
    {
      field: 'reportType',
      headerName: t('report_configs_type_header'),
      flex: 1,
      minWidth: 200,
      valueGetter: (params: GridValueGetterParams): string | undefined => {
        return params.row.reportSettings?.reportType;
      },
      renderCell: renderReportType,
    },
    {
      field: 'reportOccurrence',
      headerName: t('report_configs_occurrence_header'),
      flex: 0.8,
      minWidth: 120,
      renderCell: renderOccurrence,
    },
    {
      field: 'deviceGroup',
      headerName: t('report_configs_device_groups_header'),
      flex: 1,
      minWidth: 150,
      renderCell: renderDeviceGroups,
    },
    {
      field: 'emailList',
      headerName: t('report_configs_email_list_header'),
      flex: 1.5,
      minWidth: 200,
      renderCell: renderEmailList,
    },
    {
      field: 'startDate',
      headerName: t('report_configs_start_date_header'),
      flex: 1,
      minWidth: 180,
      type: 'dateTime',
      valueGetter: (params: GridValueGetterParams): string => {
        return params.value ? format(new Date(params.value), 'dd MMM yyyy HH:mm') : '';
      },
      renderCell: renderCellTooltip,
    },
    {
      field: 'endDate',
      headerName: t('report_configs_end_date_header'),
      flex: 1,
      minWidth: 180,
      type: 'dateTime',
      valueGetter: (params: GridValueGetterParams): string => {
        return params.value ? format(new Date(params.value), 'dd MMM yyyy HH:mm') : '';
      },
      renderCell: renderCellTooltip,
    },
    {
      field: 'nextExecution',
      headerName: t('report_configs_next_execution_header'),
      flex: 1,
      minWidth: 180,
      type: 'dateTime',
      valueGetter: (params: GridValueGetterParams): string => {
        return params.value ? format(new Date(params.value), 'dd MMM yyyy HH:mm') : '';
      },
      renderCell: renderCellTooltip,
    },
    {
      field: 'actions',
      headerName: t('report_configs_actions_header'),
      width: 120,
      renderCell: (params: GridRenderCellParams): ReactNode => {
        return (
          <div className="flex gap-2">
            <IconButton
              size="small"
              onClick={(): void =>
                navigate(`/app/ScheduledReportsConfig/edit/${params.row.reportName}`)
              }
              disabled={isDeleting}
              title={t('report_configs_edit_action')}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={(): void => {
                if (!isDeleting) {
                  handleDeleteClick(params.row.reportName);
                }
              }}
              disabled={isDeleting}
              title={t('report_configs_delete_action')}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <DataGrid
        rows={reportConfigRows}
        getRowId={(row: ReportConfigTableRow): number => row.rowId}
        columns={columns}
        pageSize={size}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageSizeChange={(pageSize: number): void => {
          setSize(pageSize);
        }}
        autoHeight
        hideFooterSelectedRowCount
        initialState={{
          columns: {
            columnVisibilityModel: {
              startDate: false,
              endDate: false,
            },
          },
        }}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('report_configs_delete_confirm_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('report_configs_delete_confirm_message', { reportName: reportToDelete })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            {t('report_configs_delete_cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? t('report_configs_deleting') : t('report_configs_delete_confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ScheduledReportConfigsTableProps {
  reportConfigRows: ReportConfigTableRow[];
  onRefetch: () => void;
}

type ReportConfigTableRow = ReportConfigDto & {
  rowId: number;
};

export default ScheduledReportConfigsTable;
